import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { untilDestroyed } from '@ngneat/until-destroy';

import { ConnectorConfiguration } from '@blaze-shared/connectors-domain';
import {
  CopyConnectorConfigEnvButtonComponent,
  DialogModule,
  EnvironmentIndicatorComponent,
} from '@blaze-shared/shared-feature';
import { ButtonBarComponent, PanelComponent, PanelRef } from '@blaze-shared/shared-ui';

import { BaseConnectorDialogComponent } from '../../__base-connector-dialog/base-connector-dialog.component';
import { prepareConnectorConfiguration } from '../../utils';
import { WebhookIdInputComponent } from '../../webhook-id-input/webhook-id-input.component';

import { ConnectorForm, DropboxSignConfiguration } from './dropbox-sign-connector-dialog.models';

@Component({
  selector: 'blaze-dropbox-sign-connector-dialog',
  imports: [
    AsyncPipe,
    ButtonBarComponent,
    CopyConnectorConfigEnvButtonComponent,
    DialogModule,
    EnvironmentIndicatorComponent,
    MatIconModule,
    MatSlideToggleModule,
    ReactiveFormsModule,
    PanelComponent,
    WebhookIdInputComponent,
  ],
  templateUrl: './dropbox-sign-connector-dialog.component.html',
  styleUrls: ['./dropbox-sign-connector-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'data-connector-dialog' },
})
export class DropboxSignConnectorDialogComponent
  extends BaseConnectorDialogComponent<DropboxSignConfiguration>
  implements OnInit
{
  private readonly panelRef = inject(PanelRef);

  protected readonly webhookBaseUrl = '/DropboxSign/webhook';

  serviceName = 'DropboxSign';
  connectorForm: FormGroup<ConnectorForm>;
  isLoading = false;

  constructor() {
    super();
    this.connectorForm = new FormGroup({
      connectorName: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      apiKey: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      testMode: new FormControl(false),
      useWebhook: new FormControl(false),
      webhookId: new FormControl(''),
    });

    this.initUseWebhookCleanup(this.connectorForm);
  }

  ngOnInit(): void {
    if (this.connector) {
      this.fillForm(this.getConfiguration(this.connector));
    }
  }

  saveConnector(): void {
    if (this.connectorForm.invalid) {
      return;
    }

    const { connectorName, apiKey, testMode, webhookId } = this.connectorForm.value;

    if (!connectorName || !apiKey) {
      return;
    }

    const connectorConfig = prepareConnectorConfiguration(
      connectorName,
      this.template?.id,
      this.accountId,
      this.connector,
      {
        apiKey,
        testMode: testMode || false,
        id: webhookId ?? '',
      },
    );

    this.isLoading = true;
    this.saveOrUpdateConnector(connectorConfig, this.connectorId)
      .pipe(untilDestroyed(this))
      .subscribe((connector) => {
        if (connector) {
          this.panelRef.close();
        }
        this.isLoading = false;
        this.cdr.markForCheck();
      });
  }

  getConfiguration(connector?: ConnectorConfiguration): DropboxSignConfiguration {
    const connectorId = connector?.id ?? '';
    const connectorName = connector?.name ?? '';
    const configuration = connector?.configuration ?? {};

    const apiKey = String(configuration?.apiKey ?? '');
    const testMode = Boolean(configuration?.testMode || false);
    const id = String(configuration?.id ?? '');

    return {
      connectorId,
      connectorName,
      apiKey,
      testMode,
      id,
    };
  }

  fillForm(configuration: DropboxSignConfiguration): void {
    const { connectorId, connectorName, apiKey, testMode, id } = configuration;

    const useWebhook = !!id;

    this.connectorId = connectorId;

    this.connectorForm.patchValue(
      {
        connectorName,
        apiKey,
        testMode,
        useWebhook,
        webhookId: id,
      },
      { emitEvent: false },
    );
  }
}
